/**
 * KAN-754: панель открытой ручной категории над списком товаров: пометка
 * "Добавлена вручную", у владельца "Переименовать" и "Удалить" (с
 * подтверждением). Категорию из фида не показывает.
 */
import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { useCatalogMutations } from '@/hooks/useCatalog';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { CatalogCategory } from '@/types/catalog';
import { CategoryFormDialog } from './CategoryFormDialog';
import { ManualBadge } from './ManualBadge';

interface Props {
  category: CatalogCategory;
  canEdit: boolean;
  /** Категория удалена: сбросить выбор в каталоге. */
  onDeleted: () => void;
}

export function CategoryActions({ category, canEdit, onDeleted }: Props) {
  const { deleteCategory } = useCatalogMutations();
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!category.manual) return null;

  const remove = (): void =>
    deleteCategory.mutate(category.id, { onSuccess: onDeleted });

  return (
    <div data-testid="category-actions" className="space-y-1 rounded-lg border bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 truncate text-sm font-medium text-gray-900" title={category.name}>
          {category.name}
        </span>
        <ManualBadge label="Добавлена вручную" />
        {canEdit && (
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setRenameOpen(true)}>
              <Pencil className="mr-1.5 h-4 w-4" />
              Переименовать
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:text-red-700"
              disabled={deleteCategory.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Удалить
            </Button>
          </div>
        )}
      </div>
      {deleteCategory.isError && (
        <p className="text-xs text-red-700">
          {extractErrorMessage(deleteCategory.error, 'Не удалось удалить категорию')}
        </p>
      )}
      {canEdit && (
        <>
          <CategoryFormDialog open={renameOpen} onOpenChange={setRenameOpen} category={category} />
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            testId="category-delete-confirm"
            view={{
              title: `Удалить категорию "${category.name}"?`,
              description: 'Товары и подкатегории останутся в каталоге без этой категории.',
              confirm: 'Удалить',
            }}
            onConfirm={remove}
          />
        </>
      )}
    </div>
  );
}
